export const colors = {
  canvas: '#ffffff',
  surface: '#ffffff',
  surfaceAlt: '#f2f4f7',
  surfaceHighlight: '#f3f6ff',
  border: '#d7dce3',
  borderStrong: '#b9c0ca',
  text: '#1f2937',
  muted: '#667085',
  lighter: '#87909e',
  primary: '#0b5cff',
  primaryDark: '#0647c6',
  primarySoft: '#edf3ff',
  info: '#0b5cff',
  infoSoft: '#edf3ff',
  success: '#087b5f',
  successSoft: '#e8f7ef',
  warning: '#9a5b00',
  warningSoft: '#fff5df',
  danger: '#b42318',
  dangerSoft: '#fff0ed',
};

export const shadows = {
  card: '0 1px 2px rgba(16, 24, 40, 0.05)',
  raised: '0 18px 42px rgba(16, 24, 40, 0.18)',
};

export const styles = {
  page: {
    background: colors.canvas,
    color: colors.text,
    minHeight: '100vh',
    padding: '0 16px 48px',
  },
  shell: {
    margin: '0 auto',
    maxWidth: 1184,
    padding: '20px 0 56px',
  },
  topLine: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  brand: {
    alignItems: 'center',
    color: colors.text,
    display: 'inline-flex',
    fontSize: 15,
    fontWeight: 800,
    gap: 10,
    textDecoration: 'none',
  },
  nav: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    maxWidth: '100%',
    paddingBottom: 2,
  },
  navLink: {
    alignItems: 'center',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    color: colors.text,
    display: 'inline-flex',
    flex: '0 0 auto',
    fontSize: 13,
    fontWeight: 700,
    minHeight: 34,
    padding: '0 10px',
    textDecoration: 'none',
  },
  intro: {
    marginBottom: 24,
  },
  introContent: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'space-between',
  },
  title: {
    color: colors.text,
    fontSize: 30,
    fontWeight: 800,
    lineHeight: 1.16,
    margin: '0 0 8px',
  },
  subtitle: {
    color: colors.muted,
    fontSize: 16,
    lineHeight: 1.5,
    margin: 0,
    maxWidth: 720,
  },
  section: {
    marginTop: 24,
  },
  sectionHeader: {
    alignItems: 'flex-end',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1.25,
    margin: 0,
  },
  caption: {
    color: colors.muted,
    fontSize: 13,
    lineHeight: 1.45,
    margin: 0,
  },
  card: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    boxShadow: shadows.card,
  },
  cardPadding: {
    padding: 20,
  },
  grid: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
  },
  taskGrid: {
    columnGap: 16,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    rowGap: 16,
  },
  twoColumn: {
    alignItems: 'start',
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  },
  threeColumn: {
    alignItems: 'start',
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
  },
  profileInfoCard: {
    alignItems: 'flex-start',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    boxShadow: shadows.card,
    display: 'grid',
    gap: 12,
    gridTemplateColumns: '44px minmax(0, 1fr)',
    padding: 18,
  },
  profileIcon: {
    alignItems: 'center',
    background: colors.primarySoft,
    borderRadius: 8,
    color: colors.primaryDark,
    display: 'inline-flex',
    fontSize: 12,
    fontWeight: 900,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  operationWorkspace: {
    alignItems: 'start',
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  },
  operationMap: {
    background:
      'linear-gradient(135deg, rgba(11, 92, 255, 0.12), rgba(8, 123, 95, 0.12)), repeating-linear-gradient(45deg, #eef2f6 0, #eef2f6 18px, #f8fafc 18px, #f8fafc 36px)',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    minHeight: 500,
    overflow: 'hidden',
    position: 'relative',
  },
  operationMapToolbar: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    display: 'grid',
    gap: 8,
    left: 16,
    padding: 12,
    position: 'absolute',
    top: 16,
    width: 'min(280px, calc(100% - 32px))',
  },
  operationMapMarker: {
    background: colors.primary,
    border: '3px solid #ffffff',
    borderRadius: 999,
    boxShadow: shadows.card,
    height: 18,
    position: 'absolute',
    width: 18,
  },
  operationSelectedList: {
    display: 'grid',
    gap: 10,
    margin: 0,
    padding: 0,
  },
  registrationAppBar: {
    alignItems: 'center',
    background: colors.surface,
    borderBottom: `1px solid ${colors.border}`,
    display: 'flex',
    gap: 16,
    justifyContent: 'space-between',
    margin: '-20px calc((100vw - min(1184px, 100vw - 32px)) / -2) 32px',
    minHeight: 72,
    padding: '0 max(16px, calc((100vw - 1184px) / 2))',
    position: 'sticky',
    top: 0,
    zIndex: 12,
  },
  registrationAppBarTitle: {
    color: colors.text,
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.3,
    margin: 0,
  },
  registrationLayout: {
    alignItems: 'start',
    display: 'grid',
    gap: 32,
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
    justifyContent: 'center',
    margin: '0 auto',
    maxWidth: 1012,
  },
  registrationStepsPanel: {
    background: colors.surface,
    display: 'grid',
    gap: 4,
  },
  registrationStepButton: {
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    borderRadius: 6,
    color: colors.text,
    cursor: 'pointer',
    display: 'grid',
    gap: 12,
    gridTemplateColumns: '28px minmax(0, 1fr)',
    padding: '12px 8px',
    textAlign: 'left',
    width: '100%',
  },
  registrationStepIndex: {
    alignItems: 'center',
    borderRadius: 999,
    color: colors.primary,
    display: 'inline-flex',
    fontSize: 14,
    fontWeight: 800,
    height: 28,
    justifyContent: 'center',
    width: 28,
  },
  registrationFormShell: {
    minWidth: 0,
  },
  registrationFormTitle: {
    color: colors.text,
    fontSize: 32,
    fontWeight: 800,
    lineHeight: 1.16,
    margin: '0 0 24px',
  },
  registrationFormCard: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  registrationFormBody: {
    display: 'grid',
    gap: 18,
    padding: 24,
  },
  registrationBottomBar: {
    alignItems: 'center',
    background: colors.surface,
    borderTop: `1px solid ${colors.border}`,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
    padding: 16,
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  tableWrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    overflowX: 'auto',
  },
  propertyCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    minWidth: 0,
  },
  propertyText: {
    color: colors.text,
    display: 'block',
    fontSize: 14,
    lineHeight: 1.45,
    overflowWrap: 'anywhere',
  },
  cozyLink: {
    color: colors.primary,
    display: 'block',
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.45,
    overflowWrap: 'anywhere',
    textDecoration: 'none',
  },
  iconButton: {
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    borderRadius: 999,
    color: colors.muted,
    cursor: 'pointer',
    display: 'inline-flex',
    flex: '0 0 auto',
    height: 32,
    justifyContent: 'center',
    padding: 0,
    width: 32,
  },
  mobileTableStack: {
    display: 'grid',
    gap: 12,
    width: '100%',
  },
  mobileTableRow: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    boxShadow: shadows.card,
    display: 'grid',
    gap: 12,
    padding: 14,
    width: '100%',
  },
  mobileTableCell: {
    display: 'grid',
    gap: 4,
    minWidth: 0,
  },
  mobileTableLabel: {
    color: colors.muted,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0,
    lineHeight: 1.35,
    textTransform: 'uppercase',
  },
  mobileTableValue: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 1.45,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  table: {
    borderCollapse: 'collapse',
    minWidth: 840,
    width: '100%',
  },
  th: {
    background: colors.surfaceAlt,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.muted,
    fontSize: 12,
    fontWeight: 800,
    padding: '12px 14px',
    textAlign: 'left',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  td: {
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: 14,
    lineHeight: 1.45,
    padding: '14px',
    verticalAlign: 'top',
  },
  formGrid: {
    display: 'grid',
    gap: 14,
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
  },
  field: {
    background: colors.surface,
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: 4,
    boxSizing: 'border-box',
    color: colors.text,
    font: 'inherit',
    minHeight: 42,
    padding: '0 12px',
    width: '100%',
  },
  label: {
    color: colors.text,
    display: 'block',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.35,
    marginBottom: 6,
  },
  helper: {
    color: colors.muted,
    display: 'block',
    fontSize: 12,
    lineHeight: 1.4,
    marginTop: 6,
  },
  divider: {
    border: 0,
    borderTop: `1px solid ${colors.border}`,
    margin: '16px 0',
  },
  stickyFooter: {
    alignItems: 'center',
    background: colors.surface,
    borderTop: `1px solid ${colors.border}`,
    bottom: 0,
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    margin: '24px -20px -20px',
    padding: 16,
    position: 'sticky',
  },
  drawerBackdrop: {
    background: 'rgba(16, 24, 40, 0.46)',
    bottom: 0,
    left: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 40,
  },
  drawer: {
    background: colors.surface,
    bottom: 0,
    boxShadow: shadows.raised,
    maxWidth: 600,
    overflowY: 'auto',
    padding: 24,
    position: 'fixed',
    right: 0,
    top: 0,
    width: 'calc(100% - 32px)',
    zIndex: 41,
  },
  modal: {
    background: colors.surface,
    borderRadius: 8,
    boxShadow: shadows.raised,
    display: 'flex',
    flexDirection: 'column',
    left: '50%',
    maxHeight: 'calc(100vh - 48px)',
    maxWidth: 520,
    overflow: 'hidden',
    padding: 24,
    position: 'fixed',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 32px)',
    zIndex: 42,
  },
  modalBody: {
    color: colors.text,
    lineHeight: 1.55,
    overflowY: 'auto',
    paddingRight: 2,
  },
  modalActions: {
    alignItems: 'center',
    background: colors.surface,
    borderTop: `1px solid ${colors.border}`,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'flex-end',
    margin: '20px -24px -24px',
    padding: 16,
  },
};

export const buttonStyle = (variant = 'primary') => {
  const variants = {
    danger: {
      background: colors.surface,
      border: colors.danger,
      color: colors.danger,
    },
    primary: {
      background: colors.primary,
      border: colors.primary,
      color: '#ffffff',
    },
    quiet: {
      background: 'transparent',
      border: 'transparent',
      color: colors.primary,
    },
    secondary: {
      background: colors.surface,
      border: colors.borderStrong,
      color: colors.text,
    },
  };
  const selected = variants[variant] || variants.primary;
  const isQuiet = variant === 'quiet';

  return {
    alignItems: 'center',
    background: selected.background,
    border: `1px solid ${selected.border}`,
    borderRadius: isQuiet ? 0 : 6,
    color: selected.color,
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 800,
    gap: 8,
    justifyContent: 'center',
    lineHeight: 1.2,
    minHeight: isQuiet ? 0 : 40,
    padding: isQuiet ? 0 : '0 14px',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  };
};

export const tagStyle = (kind = 'default') => {
  const palettes = {
    critical: [colors.dangerSoft, colors.danger],
    default: [colors.primarySoft, colors.primaryDark],
    neutral: [colors.surfaceAlt, colors.muted],
    success: [colors.successSoft, colors.success],
    warning: [colors.warningSoft, colors.warning],
  };
  const palette = palettes[kind] || palettes.default;

  return {
    alignItems: 'center',
    background: palette[0],
    border: `1px solid ${palette[0]}`,
    borderRadius: 999,
    color: palette[1],
    display: 'inline-flex',
    fontSize: 12,
    fontWeight: 800,
    lineHeight: '22px',
    padding: '0 10px',
    whiteSpace: 'nowrap',
  };
};
